import React from "react";
import { SEO } from "../components/SEO";
import { Target, Users, Zap, Shield, CheckCircle2, Play } from "lucide-react";

export const Sobre = () => {
  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      <SEO 
        title="Sobre Nós | RosTech" 
        description="Conheça a história e a missão da RosTech, o ERP nº 1 para oficinas mecânicas e autopeças."
        canonicalUrl="/sobre"
      />

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=2000" 
            alt="Laboratório e tecnologia" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900/80 mix-blend-multiply"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="inline-block mb-4 px-3 py-1.5 bg-blue-500/20 text-blue-200 border border-blue-400/30 rounded-full text-sm font-bold tracking-widest uppercase backdrop-blur-sm">
            Nossa História
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
            Nascidos para <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">revolucionar</span> a sua oficina.
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 font-medium">
            Conectamos tecnologia de ponta com a realidade da sua mecânica.
          </p>
        </div>
      </section>

      {/* Nossa História Text & Image */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">Como Tudo Começou</h2>
              <div className="prose prose-slate prose-lg">
                <p>
                  A RosTech nasceu da observação diária da dificuldade que os donos de oficinas enfrentavam na gestão de seus negócios. Papel, caneta, e planilhas desconexas eram a regra.
                </p>
                <p>
                  Percebemos que, enquanto a tecnologia dos próprios carros avançava em velocidade assustadora, as ferramentas administrativas para quem consertava esses carros estavam paradas no tempo. Em oficinas, peças sumiam, os orçamentos não eram padronizados e o controle do lucro real era quase nulo.
                </p>
                <p>
                  Reunimos uma equipe de especialistas em software, apaixonados por carros e engenharia, e criamos o <strong className="text-blue-700">ERP RosTech</strong>. Focado 100% no setor automotivo, nossa plataforma foi construída lado a lado com mecânicos, gestores e especialistas em dados.
                </p>
              </div>
            </div>
            <div className="lg:w-1/2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white transform rotate-2">
                <img 
                  src="https://images.unsplash.com/photo-1504222490345-c075b6008014?auto=format&fit=crop&q=80&w=1200" 
                  alt="Oficina Mecânica" 
                  className="w-full h-auto object-cover transform transition-transform hover:scale-105 duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent flex items-end p-8">
                   <div className="text-white">
                      <p className="font-bold text-lg">O chão de fábrica inspirou nossa tecnologia.</p>
                   </div>
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 transform -rotate-3">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-black text-xl">
                       +5k
                    </div>
                    <div>
                       <p className="text-slate-900 font-bold text-sm">Oficinas Atendidas</p>
                       <p className="text-slate-500 text-xs">Em todo o Brasil</p>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-slate-900 text-white relative h-auto">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6">Gestão que você pode ver.</h2>
            <p className="text-xl text-slate-400">
              Assista como a plataforma RosTech funciona na prática, transformando o caos de uma oficina em uma operação previsível e altamente rentável.
            </p>
          </div>

          {/* Video Placeholder using HTML5 video and cover image */}
          <div className="relative w-full max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-[0_20px_50px_-15px_rgba(0,0,0,0.5)] border border-slate-700 group cursor-pointer aspect-video bg-black flex items-center justify-center">
            {/* We will embed a youtube placeholder for visual */}
             <iframe 
               width="100%" 
               height="100%" 
               src="https://www.youtube-nocookie.com/embed/zpOULjyy-n8?rel=0&controls=1&showinfo=0" 
               title="Demonstração RosTech" 
               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
               allowFullScreen
               className="absolute inset-0 w-full h-full border-0"
             ></iframe>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Nossos Valores</h2>
            <p className="text-lg text-slate-500">
              Acreditamos que um software é tão bom quanto as pessoas e os princípios por trás dele.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
             {/* Card 1 */}
             <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-14 h-14 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center mb-6">
                   <Target className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Foco no Cliente</h3>
                <p className="text-slate-600 leading-relaxed">
                   Seu sucesso é o nosso sucesso. Desenvolvemos cada funcionalidade baseados no feedback real de oficinas, funilarias e lojas.
                </p>
             </div>

             {/* Card 2 */}
             <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-14 h-14 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center mb-6">
                   <Zap className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Agilidade e Inovação</h3>
                <p className="text-slate-600 leading-relaxed">
                   Atualizamos nossa plataforma constantemente. A tecnologia muda rápido, e nós garantimos que você estará sempre à frente.
                </p>
             </div>

             {/* Card 3 */}
             <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-14 h-14 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-6">
                   <Shield className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Segurança Total</h3>
                <p className="text-slate-600 leading-relaxed">
                   Os dados financeiros e de clientes da sua empresa são o seu maior ativo. Utilizamos criptografia de nível bancário para protegê-los.
                </p>
             </div>

             {/* Card 4 */}
             <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className="w-14 h-14 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center mb-6">
                   <Users className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Parceria Real</h3>
                <p className="text-slate-600 leading-relaxed">
                   Não queremos ser apenas o seu sistema, queremos ser seu parceiro estratégico de crescimento. O nosso suporte humano atesta isso.
                </p>
             </div>
          </div>
        </div>
      </section>

      {/* Culture Image Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                 <img 
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800" 
                    alt="Processos e métricas" 
                    className="rounded-3xl shadow-xl border border-slate-200"
                 />
              </div>
              <div className="pl-0 md:pl-10 mt-10 md:mt-0">
                 <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">A Cultura por Trás do Código</h2>
                 <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                    Nossa equipe é formada por pessoas que não têm medo de sujar as mãos. Literalmente. Regularmente, nossos programadores passam o dia em oficinas mecânicas ouvindo ferramentas operando e pneus sendo trocados, para entender exatamente a dor de quem vai apertar o botão no final do dia.
                 </p>
                 <ul className="space-y-4">
                   <li className="flex items-center text-slate-700 font-medium">
                     <CheckCircle2 className="text-blue-600 w-6 h-6 mr-3" /> Imersão no problema do cliente.
                   </li>
                   <li className="flex items-center text-slate-700 font-medium">
                     <CheckCircle2 className="text-blue-600 w-6 h-6 mr-3" /> Transparência nos resultados e métricas.
                   </li>
                   <li className="flex items-center text-slate-700 font-medium">
                     <CheckCircle2 className="text-blue-600 w-6 h-6 mr-3" /> Autonomia para inovar e propor soluções.
                   </li>
                 </ul>
              </div>
           </div>
        </div>
      </section>

      {/* CEO Message Section */}
      <section className="py-24 bg-slate-50 border-t border-slate-100">
         <div className="max-w-4xl mx-auto px-6">
           <div className="bg-slate-900 text-white rounded-[32px] p-10 md:p-16 flex flex-col items-center text-center shadow-2xl relative overflow-hidden">
              {/* Decor */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full opacity-20 blur-3xl translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full opacity-20 blur-3xl -translate-x-1/2 translate-y-1/2"></div>
              
              <img
                src={`${process.env.PUBLIC_URL || ''}/images/rosiana-lacerda.png`}
                className="w-28 h-28 object-cover object-[center_22%] rounded-full border-4 border-slate-800 ring-2 ring-blue-500 mb-8 relative z-10"
                alt="Rosiana Lacerda"
              />
              
              <p className="text-3xl font-serif italic mb-8 font-light relative z-10 leading-snug text-slate-200">
                "Não vendemos apenas software. Ensinamos sua oficina a ser um negócio altamente lucrativo."
              </p>
              
              <div className="relative z-10">
                 <h4 className="font-black text-xl tracking-wide mb-1">Rosiana Lacerda</h4>
                 <div className="text-blue-400 font-semibold text-sm mb-2 uppercase tracking-widest">CEO RosTech & Mentora de Oficinas</div>
                 <div className="text-slate-400 text-xs font-mono">@rosiana_lacerda</div>
              </div>
           </div>
         </div>
      </section>

    </div>
  );
};
